import type { TimestampInMillis } from './dates';
import type { UUID } from './identifier';

export const DEFAULT_FUEL_CONSUMPTION = 8; // assuming 8 km/l
export const DEFAULT_GAS_PRICE = 4.567;
export const LITERS_PER_IDLE_HOUR = 1.5; // assuming 1,5 l/h
export const LITERS_PER_IDLE_SECOND = LITERS_PER_IDLE_HOUR / (60 * 60);

export interface FuelTransaction {
  id: UUID;
  createdBy: string;
  transactionCode?: string;
  timeInMillis: TimestampInMillis;
  vehicleId: UUID;
  licensePlate?: string;
  fuelType: string;
  totalCost: number;
  fuelLiters: number;
  cardDrivenMeters: number;
  cobliDrivenMeters?: number;
  vehicleModel?: string;
  deletedAt?: TimestampInMillis;
}

interface FuelTransactionBodyContentBase {
  transactionTime: string;
  vehicleId: string;
  odometerInKm?: number;
  driverName?: string;
  fuelType: string;
  totalCost: number;
  quantityInLiters: number;
  merchant?: Merchant;
}

export type FuelTransactionAttachment =
  | {
      /** Base64-encoded receipt bytes, without the `data:...;base64,` prefix. */
      fileBase64: string;
      /** MIME type of the receipt. */
      fileContentType: string;
    }
  | {
      fileBase64?: undefined;
      fileContentType?: undefined;
    };

/** Optional odometer photo attachment — same base64 envelope as the receipt. */
export interface FuelTransactionOdometerAttachment {
  odometerFileBase64?: string;
  odometerFileContentType?: string;
}

export type FuelTransactionBodyContent = FuelTransactionBodyContentBase & FuelTransactionAttachment & FuelTransactionOdometerAttachment;

export interface Merchant {
  name?: string;
  state?: string;
  city?: string;
  cnpj?: string;
}

export interface TransactionVehicleGroup {
  id: UUID;
  name: String;
}

export interface TransactionVehicle {
  id: UUID;
  licensePlate: string;
  model: string;
  group: TransactionVehicleGroup;
  alias?: string;
}

/** Same values as the frentista's `ConferenceStatus` enum (BE-18); computed on read, absent until the listing sends it. */
export type ConferenceStatus = 'AWAITING_EVALUATION' | 'NO_DIVERGENCE' | 'NEEDS_REVIEW' | 'REVIEWED' | 'MARKED_SUSPICIOUS';

export interface Transaction {
  id: UUID;
  transactionTime: number;
  transactionCode: string;
  vehicle: TransactionVehicle;
  fuelType: string;
  costPerLiter: number;
  fuelLiters: number;
  totalCost: number;
  cobliDrivenKms: number;
  createdBy: string;
  creatorId?: UUID;
  /** The driver of the fuel stop (`driver_name`), whatever the origin: the app fills it from the session, the others from the entry. */
  driverName?: string | null;
  totalCount: number;
  fileUrl: string | null;
  /** How the entry got in. Null on rows written before the column existed. */
  source?: string | null;
  conferenceStatus?: ConferenceStatus;
}

/**
 * How the Origin column and the drawer label a fuel entry.
 *
 * `driver_app` is kept for rows written before the `source` column existed: there the AI and the
 * manual flow are indistinguishable, so they must not be guessed into either one.
 */
export type OriginType = 'manual' | 'driver_app' | 'driver_app_ai' | 'driver_app_manual' | 'integration';

/**
 * Narrows an app-created entry into the AI or the manual flow.
 *
 * Anything the panel does not recognise — a legacy null, or an origin a newer backend starts
 * writing — falls back to the generic app label instead of breaking the row.
 */
export const driverAppOriginType = (source?: string | null): OriginType => {
  if (source === 'DriverAppManual') return 'driver_app_manual';
  if (source === 'DriverAppAi') return 'driver_app_ai';
  return 'driver_app';
};

export interface PagedTransactions {
  content: Transaction[];
  number: number;
  size: number;
  totalPages: number;
  totalElements: number;
}

export interface TransactionsSummary {
  avgFuelConsumption: number;
  avgCostPerKm: number;
  sumTotalCost: number;
}

export interface TransactionStatsByVehicleSummary {
  sumCobliDrivenKms: number;
  avgCostPerLiter: number;
  sumFuelLiters: number;
}

/**
 * Resposta de `/transactions/stats-by-vehicle/summary/grouped`: um item por bucket
 * (semana/mês). Buckets sem dados não vêm no array — o join por `startInterval`
 * precisa tratar a ausência.
 */
export interface TransactionStatsByVehicleSummaryByPeriod {
  startInterval: Date;
  endInterval: Date;
  isCompleted: boolean;
  /** Backend nomeia este campo `sum_driven_kms` no `/summary/grouped`, diferente do `/summary`
   * agregado que usa `sum_cobli_driven_kms`. Mantemos o nome do contrato. */
  sumDrivenKms: number;
  avgCostPerLiter: number;
  sumFuelLiters: number;
}

export interface TransactionsAvgFuelConsumptionAndCostPerKm {
  startInterval: Date;
  endInterval: Date;
  isCompleted: boolean;
  avgFuelConsumption: number;
  avgCostPerKm: number;
  vehicleLicensePlate: string;
  chartGroup: string;
}

export interface TransactionsSumTotalCostByFuelType {
  startInterval: Date;
  endInterval: Date;
  isCompleted: boolean;
  fuelType: string;
  totalCost: number;
  totalLiters: number;
}

/** 1 is the third of the fleet's comparable vehicles that consumes the least (best km/L), 3 the one that consumes the most. */
export type ConsumptionTercile = 1 | 2 | 3;

/** Why a (vehicle, fuel) row came without a consumption tercile. */
export type ConsumptionTercileUnavailableReason =
  /** The fuel is 'Outros', which mixes products whose km/L does not compare. */
  | 'FUEL_NOT_COMPARABLE'
  /** No refuel with a driven distance in the period. */
  | 'NO_DISTANCE'
  /** Too few refuels with a driven distance in the period. */
  | 'INSUFFICIENT_SAMPLE'
  /** Too few comparable vehicles of the same model family and fuel in the fleet, or a model with no family. */
  | 'NO_COMPARABLE_PEERS';

export interface TransactionStatsByVehicle {
  vehicleId: UUID;
  vehicleLicensePlate: string;
  vehicleModel: string;
  /** The model family the row is compared against (Strada Cs, Strada Endurance → Strada); null when the model has no family. */
  vehicleModelFamilyId: UUID | null;
  vehicleModelFamilyName: string | null;
  vehicleAlias: string | null;
  vehicleGroup: string;
  vehicleGroupName: string;
  sumTotalCost: number;
  avgCostPerLiter: number;
  /** Null when no distance was covered in the period: a cost over 0 km has no value. */
  avgCostPerKm: number | null;
  sumFuelLiters: number;
  /** Null when no refuel in the period has a driven distance. */
  sumCobliDrivenKms: number | null;
  /** Null when no distance was measured; 0 when the vehicle was measured and did not move. */
  consumptionAvg: number | null;
  consumptionGoal: number;
  fuelType: string;
  /** Refuels of this (vehicle, fuel) row in the period. */
  transactionCount: number;
  /** Refuels of this row with a driven distance: the sample the tercile is computed on. */
  measuredTransactionCount: number;
  /** Null when the row has no comparison; the reason says why. */
  consumptionTercile: ConsumptionTercile | null;
  consumptionTercileUnavailableReason: ConsumptionTercileUnavailableReason | null;
  /** Vehicles of the same model family and fuel the tercile was computed among; null without a tercile. */
  comparableVehicleCount: number | null;
  /** Rows of each tercile under the screen filters, ignoring the tercile filter; repeated on every row. */
  leastConsumingCount: number;
  middleConsumingCount: number;
  mostConsumingCount: number;
  noTercileCount: number;
  /** Rows matching the filters, for pagination; not the row's refuel count. */
  totalCount: number;
}

export interface PagedTransactionStatsByVehicle {
  content: TransactionStatsByVehicle[];
  number: number;
  size: number;
  totalPages: number;
  totalElements: number;
}

export interface TransactionsHistoryBodyContent {
  startDate: Date;
  endDate: Date;
  searchQuery?: String;
  fuelTypes?: String[];
  vehicleIds?: UUID[];
  vehicleGroupIds: UUID[];
  vehicleSizes?: String[];
  tagNames?: String[];
  /** Only the transactions with one of these statuses; empty or absent means all. */
  conferenceStatuses?: ConferenceStatus[];
  vehicleModels?: string[];
  /** Only stats-by-vehicle reads it: keeps the rows in these terciles. The tercile counts ignore it. */
  consumptionTerciles?: ConsumptionTercile[];
  sortField: String;
  sortDirection: String;
  page: Number;
  size: Number;
}

export interface TransactionSummaryBodyContent {
  startDate: Date;
  endDate: Date;
  searchQuery?: string;
  fuelTypes?: string[];
  vehicleIds?: UUID[];
  vehicleGroupIds: UUID[];
  vehicleSizes?: string[];
  tagNames?: string[];
  vehicleModels?: string[];
}

export type GroupPeriod = 'WEEK' | 'MONTH';

export interface TransactionSummaryBodyContentByPeriodType extends TransactionSummaryBodyContent {
  groupPeriod: GroupPeriod;
}

export const toFormattedComsumptionInLiters = (value: number, minimumFractionDigits: number, maximumFractionDigits: number): string =>
  Intl.NumberFormat('pt-BR', {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(value);
